import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import LayoutCreateEvent from '@/components/layout/LayoutCreateEvent';
import LayoutDashboardOrganizer from '@/components/layout/LayoutDashboardOrganizer';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuContext } from '@/hooks/organizerHooks/useMenuContext';
import PaymentTablePage from '@/pages/dashboard/payment/paymentTable.page';
import { dashboardStartPath } from '@/utils/menuVisibility';
import { SuspensedView } from './SuspensedView';

const EventTablePage = lazy(() => import('../pages/dashboard/events/eventTable.page'));
const EventBasicInformation = lazy(() => import('../pages/dashboard/events/eventBasicInformation'));
const EventCategories = lazy(() => import('../pages/dashboard/events/eventCategories'));
const EventDescription = lazy(() => import('../pages/dashboard/events/eventDescription'));
const EventGateway = lazy(() => import('../pages/dashboard/events/eventGateway'));
const EventPersonalization = lazy(() => import('../pages/dashboard/events/EventPersonalization'));
const EventTicket = lazy(() => import('../pages/dashboard/events/eventTicket'));
const EventCupons = lazy(() => import('../pages/dashboard/events/eventCupons'));
const EventWods = lazy(() => import('../pages/dashboard/events/eventWods'));
const EventPontuacao = lazy(() => import('../pages/dashboard/events/eventPontuacao'));
const EventPublish = lazy(() => import('../pages/dashboard/events/eventPublish'));
const EventAddress = lazy(() => import('../pages/dashboard/events/eventAddress'));
const OrganizerOauth = lazy(() => import('../pages/dashboard/organizer/organizerOauth.page'));
const PanelOrganizer = lazy(() => import('../pages/dashboard/organizer/panelOrganizer'));
const EventNotifications = lazy(
  () => import('../pages/dashboard/organizer/eventNotifications.page')
);
const Leaderboard = lazy(() => import('../pages/dashboard/organizer/leaderboard'));
const InscricaoAtleta = lazy(() => import('../pages/dashboard/organizer/inscricaoAtleta'));
const SubirResultados = lazy(() => import('../pages/dashboard/organizer/subirResultados'));
const Baterias = lazy(() => import('../pages/dashboard/organizer/baterias'));
const Atletas = lazy(() => import('../pages/dashboard/organizer/atletas'));
const PaymentPage = lazy(() => import('../components/layout/LayoutPayment'));
const UsersTablePage = lazy(() => import('../pages/dashboard/users/allUsers.page'));
const MovementsLibraryPage = lazy(
  () => import('../pages/dashboard/movements/movementsLibrary.page')
);
const NotificationsPage = lazy(() => import('../pages/dashboard/notifications/notifications.page'));
const MyProfilePage = lazy(() => import('../pages/dashboard/profile/myProfile.page'));
const EventsCardsPage = lazy(() => import('../pages/dashboard/events/eventsCards.page'));
const MyInscriptionsPage = lazy(() => import('../pages/dashboard/athlete/myInscriptions.page'));
const OrganizerOauthResponse = lazy(
  () => import('../pages/dashboard/organizer/organizerOauthResponse.page')
);
const PaymentRedirectMercadoPago = lazy(
  () => import('../pages/dashboard/payment/paymentRedirectMercadoPago.page')
);
const InscriptionFinishedPage = lazy(() => import('@/pages/events/inscriptionFinished'));

// /dashboard leva para a tabela (quem tem evento próprio ou delegado) ou para a Arena; espera o
// usuário e os eventos dele carregarem para não errar o destino.
const DashboardHome = () => {
  const { currentUser } = useAuth();
  const menuContext = useMenuContext();
  if (!currentUser || menuContext.isLoading) {
    return null;
  }
  return <Navigate to={dashboardStartPath(menuContext)} replace />;
};

const UserLoggedRoutes = () => {
  return (
    <Routes>
      <Route index element={<DashboardHome />} />

      <Route element={<LayoutCreateEvent />}>
        <Route
          path="evento/cadastrar/:eventId"
          element={
            <SuspensedView>
              <EventBasicInformation />
            </SuspensedView>
          }
        />

        <Route
          path="evento/cadastrar/:eventId/endereco"
          element={
            <SuspensedView>
              <EventAddress />
            </SuspensedView>
          }
        />

        <Route
          path="evento/cadastrar/:eventId/categorias"
          element={
            <SuspensedView>
              <EventCategories />
            </SuspensedView>
          }
        />

        <Route
          path="evento/cadastrar/:eventId/descricao"
          element={
            <SuspensedView>
              <EventDescription />
            </SuspensedView>
          }
        />

        <Route
          path="evento/cadastrar/:eventId/gateway-pagamento"
          element={
            <SuspensedView>
              <EventGateway />
            </SuspensedView>
          }
        />
        <Route
          path="evento/cadastrar/:eventId/personalizacao"
          element={
            <SuspensedView>
              <EventPersonalization />
            </SuspensedView>
          }
        />

        <Route
          path="evento/cadastrar/:eventId/ticket"
          element={
            <SuspensedView>
              <EventTicket />
            </SuspensedView>
          }
        />

        <Route
          path="evento/cadastrar/:eventId/cupom"
          element={
            <SuspensedView>
              <EventCupons />
            </SuspensedView>
          }
        />

        <Route
          path="evento/cadastrar/:eventId/publicar"
          element={
            <SuspensedView>
              <EventPublish />
            </SuspensedView>
          }
        />
        <Route
          path="evento/cadastrar/:eventId/wod"
          element={
            <SuspensedView>
              <EventWods />
            </SuspensedView>
          }
        />
        <Route
          path="evento/cadastrar/:eventId/pontuacao"
          element={
            <SuspensedView>
              <EventPontuacao />
            </SuspensedView>
          }
        />
      </Route>

      <Route element={<LayoutDashboardOrganizer />}>
        <Route
          path="evento/:eventId/dashboard/painel-organizador"
          element={
            <SuspensedView>
              <PanelOrganizer />
            </SuspensedView>
          }
        />

        <Route
          path="evento/:eventId/dashboard/atletas"
          element={
            <SuspensedView>
              <Atletas />
            </SuspensedView>
          }
        />
        <Route
          path="evento/:eventId/dashboard/baterias"
          element={
            <SuspensedView>
              <Baterias />
            </SuspensedView>
          }
        />

        <Route
          path="evento/:eventId/dashboard/subir-resultados"
          element={
            <SuspensedView>
              <SubirResultados />
            </SuspensedView>
          }
        />

        <Route
          path="evento/:eventId/dashboard/notificacoes"
          element={
            <SuspensedView>
              <EventNotifications />
            </SuspensedView>
          }
        />

        <Route
          path="evento/:eventId/dashboard/leaderboard"
          element={
            <SuspensedView>
              <Leaderboard />
            </SuspensedView>
          }
        />

        <Route
          path="evento/:eventId/dashboard/categoria/:categoryId/equipes/inscricao/:inscricaoId"
          element={
            <SuspensedView>
              <InscricaoAtleta />
            </SuspensedView>
          }
        />

        <Route
          path="evento/:eventId/dashboard/categoria/:categoryId/equipes/inscricao/:inscricaoId/inscricao-atleta"
          element={
            <SuspensedView>
              <InscriptionFinishedPage />
            </SuspensedView>
          }
        />
      </Route>

      <Route element={<DashboardLayout />}>
        <Route
          path="organizador/oauth"
          element={
            <SuspensedView>
              <OrganizerOauth />
            </SuspensedView>
          }
        />

        <Route
          path="organizador/oauth-response"
          element={
            <SuspensedView>
              <OrganizerOauthResponse />
            </SuspensedView>
          }
        />

        <Route
          path="evento/tabela"
          element={
            <SuspensedView>
              <EventTablePage />
            </SuspensedView>
          }
        />

        {/* Payment Routes */}

        <Route
          path="pagamento/tabela"
          element={
            <SuspensedView>
              <PaymentTablePage />
            </SuspensedView>
          }
        />

        <Route
          path="evento/:id/pagamento"
          element={
            <SuspensedView>
              <PaymentPage />
            </SuspensedView>
          }
        >
          <Route
            path="redirect-mp"
            element={
              <SuspensedView>
                <PaymentRedirectMercadoPago />
              </SuspensedView>
            }
          />
        </Route>

        <Route
          path="movimentos"
          element={
            <SuspensedView>
              <MovementsLibraryPage />
            </SuspensedView>
          }
        />

        <Route
          path="notificacoes"
          element={
            <SuspensedView>
              <NotificationsPage />
            </SuspensedView>
          }
        />

        <Route
          path="usuarios/todos"
          element={
            <SuspensedView>
              <UsersTablePage />
            </SuspensedView>
          }
        />

        <Route
          path="eventos"
          element={
            <SuspensedView>
              <EventsCardsPage />
            </SuspensedView>
          }
        />

        <Route
          path="minhas-inscricoes"
          element={
            <SuspensedView>
              <MyInscriptionsPage />
            </SuspensedView>
          }
        />

        <Route
          path="meu-perfil"
          element={
            <SuspensedView>
              <MyProfilePage />
            </SuspensedView>
          }
        />
      </Route>
    </Routes>
  );
};

export { UserLoggedRoutes };
